import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HOME_V2_COLORS } from './colors';

// Import navigation icons
import DailySparksIcon from '../../../assets/icons/home-v2/daily-sparks.svg';
import LibraryIcon from '../../../assets/icons/home-v2/library.svg';
import ExpertTipsIcon from '../../../assets/icons/home-v2/expert-tips.svg';
import JourneyIcon from '../../../assets/icons/home-v2/journey.svg';

export const FOOTER_HEIGHT_MOBILE = 72; // iPhone SE (≤375px): 72px fixed height
export const FOOTER_HEIGHT_DESKTOP = 88; // Standard devices (≥376px): 88px fixed height

export type NavItemId = 'daily-sparks' | 'library' | 'expert-tips' | 'journey';

interface NavItem {
  id: NavItemId;
  label: string;
  icon: string;
}

const navItems: NavItem[] = [
  { id: 'daily-sparks', label: 'SPARKS', icon: DailySparksIcon },
  { id: 'library', label: 'LIBRARY', icon: LibraryIcon },
  { id: 'expert-tips', label: 'EXPERT TIPS', icon: ExpertTipsIcon },
  { id: 'journey', label: 'JOURNEY', icon: JourneyIcon },
];

/**
 * HomeV2Footer Component
 * 
 * Fixed footer navigation for Home V2 design.
 * Features:
 * - Light cyan background (#C7EDEF)
 * - Rounded top corners (36px)
 * - 4 navigation items with icons and labels
 * - Active/inactive state styling
 * - React Router navigation integration
 * 
 * Design specs from footer.svg:
 * - Height: 72px (≤375px) | 88px (≥376px)
 * - Active state: #1B2831 (dark charcoal)
 * - Inactive state: #007CAD (teal)
 */

interface HomeV2FooterProps {
  /**
   * Active tab to highlight in the footer
   * If not provided, defaults to 'daily-sparks'
   */
  activeTab?: NavItemId;
  /** Whether the check-in transition is active (triggers fade-out) */
  isTransitioning?: boolean;
}

/**
 * HomeV2FooterPlaceholder Component
 * 
 * Placeholder component that mirrors the footer's responsive height.
 * Use this in page layouts to reserve space for the fixed footer.
 * 
 * Height calculation:
 * - Mobile (≤375px): 72px fixed height + safe-area-inset-bottom
 * - Desktop (≥376px): 88px fixed height + safe-area-inset-bottom
 * 
 * Matches footer's box model exactly:
 * - Outer container: h-[72px] xs:h-[88px]
 * - Safe area inset: paddingBottom for devices with notches/gesture bars
 */
export function HomeV2FooterPlaceholder() {
  return (
    <div 
      className="h-[72px] xs:h-[88px]"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom)',
        //backgroundColor: 'red',
      }}
    />
  );
}

export default function HomeV2Footer({ activeTab = 'daily-sparks', isTransitioning = false }: HomeV2FooterProps) {
  const [activeItem, setActiveItem] = useState<NavItemId>(activeTab);
  const navigate = useNavigate();

  const handleNavClick = (itemId: NavItemId) => {
    setActiveItem(itemId);
    
    // Navigate to corresponding routes
    switch (itemId) {
      case 'daily-sparks':
        navigate('/home');
        break;
      case 'library':
        navigate('/library');
        break;
      case 'expert-tips':
        // Placeholder for future implementation
         navigate('/expert-tips');
        break;
      case 'journey':
        // Placeholder for future implementation
        navigate('/journey');
        break;
    }
  };

  return (
    <footer
      className="fixed bottom-0 left-0 right-0 z-20 h-[72px] xs:h-[88px]"
      style={{
        backgroundColor: HOME_V2_COLORS.footerBackground,
        borderRadius: '36px 36px 0 0',
        paddingBottom: 'env(safe-area-inset-bottom)',
        opacity: isTransitioning ? 0 : 1,
        transition: 'opacity 0.25s ease',
      }}
    >
      <nav
        className="h-full max-w-md lg:max-w-[640px] mx-auto px-4 sm:px-[36px] pb-[20px] xs:pb-[28px] pt-[8px] xs:pt-[12px] flex items-center justify-between"
        aria-label="Primary navigation"
      >
        {navItems.map((item) => {
          const isActive = activeItem === item.id;
          const color = isActive 
            ? HOME_V2_COLORS.navActive 
            : HOME_V2_COLORS.navInactive;

          return (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.id)}
              className="flex flex-col items-center justify-center gap-1.5 xs:gap-2 pb-[3px] xs:pb-[4px] pt-[5px] xs:pt-[7px] px-0 transition-colors min-w-[48px] h-[48px]"
              aria-label={`Navigate to ${item.label}`}
              aria-current={isActive ? 'page' : undefined}
            >
              {/* Icon with color filter */}
              <img
                src={item.icon}
                alt=""
                aria-hidden="true"
                className="w-5 h-5 xs:w-6 xs:h-6 shrink-0"
                style={{
                  // Apply color using CSS filter
                  // For white SVGs, we use brightness + sepia + hue-rotate
                  filter: isActive
                    ? 'brightness(0) saturate(100%) invert(13%) sepia(10%) saturate(1127%) hue-rotate(169deg) brightness(95%) contrast(92%)'
                    : 'brightness(0) saturate(100%) invert(35%) sepia(98%) saturate(456%) hue-rotate(156deg) brightness(92%) contrast(101%)',
                }}
              />
              
              {/* Label */}
              <span
                className="text-[8px] xs:text-[10px] font-black tracking-[0.3px] xs:tracking-[0.4px] uppercase shrink-0 whitespace-nowrap"
                style={{ color }}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </nav>
    </footer>
  );
}
